import { Helmet } from 'react-helmet-async';
import { Box, Button, Container, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Iconify from '../components/iconify';
import { RiDeleteBinLine } from 'react-icons/ri';
import { BiEdit } from 'react-icons/bi';
import useApi from '../hooks/useApi';
import { useAppContext } from '../contexts/AppContext';
import { getCountries, createCountry, updateCountry, deleteCountry } from '../services/Common/country.service';
import { useState } from 'react';
import { toast } from 'react-toastify';

export default function CountryPage() {
    const { setLoading } = useAppContext();
    const [newCountry, setNewCountry] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');

    const columns = [
        {
            field: '_id',
            headerName: 'ID',
            minWidth: 250,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
        },
        {
            field: 'name',
            headerName: 'Country Name',
            minWidth: 200,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
            renderCell: (params) =>
                editingId === params.id ? (
                    <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleUpdate(params.id); }}
                        autoFocus
                    />
                ) : (
                    params.value
                ),
        },
        {
            headerName: 'Action',
            field: 'actions',
            minWidth: 200,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderHeader: (params) => <strong>{params.colDef.headerName}</strong>,
            renderCell: (params) => (
                <>
                    {editingId === params.id ? (
                        <Button onClick={() => handleUpdate(params.id)} color="primary" variant="contained" size="small" sx={{ mr: 1 }}>
                            Save
                        </Button>
                    ) : (
                        <Button onClick={() => { setEditingId(params.id); setEditName(params.row.name); }}>
                            <BiEdit color="black" size={20} />
                        </Button>
                    )}
                    <Button onClick={() => handleDelete(params.id)}>
                        <RiDeleteBinLine color="red" size={20} />
                    </Button>
                </>
            ),
        },
    ];

    function getRowId(row) { return row._id; }

    const [filter, setFilter] = useState({ page: 0, pageSize: 10 });

    const { result, setResult } = useApi(() => getCountries(), { onLoading: (status) => setLoading(status) });

    const handleCreate = async () => {
        if (!newCountry.trim()) return toast('Enter a country name', { type: 'warning' });
        try {
            setLoading(true);
            const created = await createCountry(newCountry.trim());
            setResult(prev => [...prev, created]);
            setNewCountry('');
            toast('Country added', { type: 'success' });
        } catch (err) {
            toast(err?.response?.data?.data?.message || 'Failed to create', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (id) => {
        if (!editName.trim()) return toast('Enter a name', { type: 'warning' });
        try {
            setLoading(true);
            const updated = await updateCountry(id, editName.trim());
            setResult(prev => prev.map(c => c._id === id ? updated : c));
            setEditingId(null);
            toast('Country updated', { type: 'success' });
        } catch (err) {
            toast('Failed to update', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await deleteCountry(id);
            setResult(prev => prev.filter(c => c._id !== id));
            toast('Country deleted', { type: 'success' });
        } catch (err) {
            toast('Failed to delete', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title> Countries | Trooper </title>
            </Helmet>
            <Container>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>Countries</Typography>
                </Stack>

                <Stack direction="row" spacing={2} mb={3}>
                    <TextField
                        size="small"
                        label="New Country"
                        value={newCountry}
                        onChange={(e) => setNewCountry(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                        sx={{ minWidth: 250 }}
                    />
                    <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleCreate}>
                        Add Country
                    </Button>
                </Stack>

                <div style={{ height: '70vh', minWidth: '100%' }}>
                    {result && result.length > 0 ? (
                        <DataGrid
                            rows={result}
                            rowCount={result.length ?? 0}
                            columns={columns}
                            pageSizeOptions={[5, 10, 50]}
                            paginationModel={filter}
                            onPaginationModelChange={setFilter}
                            getRowId={getRowId}
                            rowHeight={60}
                        />
                    ) : (
                        <Box sx={{ textAlign: 'center', color: 'grey.600', fontSize: 18 }}>No countries available</Box>
                    )}
                </div>
            </Container>
        </>
    );
}
